import { useState, useEffect } from 'react'
import { Modal } from '@/components/common'
import { directorApi } from '@/services/api/director'
import type { DirectorActionItem, DirectorConfig, DirectorDecisionCreatePayload, DirectorRequestDetail } from '@/types/director'
import { extractErrorMessage, isDirectorDecisionConflict } from '@/utils/errorHandler'
import { XCircle } from 'lucide-react'
import { lessonRange } from '@/components/effective-plan/planFormat'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

function formatTableDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return iso
  }
}

interface QuickRejectModalProps {
  isOpen: boolean
  /** Fallakte v2 (P5.10): Minimal-Interface — Dashboard-Items erfüllen es ebenfalls */
  item: DirectorActionItem | null
  config: DirectorConfig | null
  onClose: () => void
  onSuccess: () => void
  /** Fallakte v2 (K7): 409-Konflikt → Liste refreschen */
  onConflict?: () => void
}

export function QuickRejectModal({ isOpen, item, config, onClose, onSuccess, onConflict }: QuickRejectModalProps) {
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionNote, setRejectionNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<DirectorRequestDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    if (isOpen && item) {
      setRejectionReason('')
      setRejectionNote('')
      setError(null)
      setDetail(null)
      setDetailLoading(true)
      directorApi.getRequestDetail(item.id)
        .then(setDetail)
        .catch(() => setDetail(null))
        .finally(() => setDetailLoading(false))
    }
  }, [isOpen, item])

  if (!item) return null

  const handleSubmit = async () => {
    if (!rejectionReason) {
      setError('Bitte wählen Sie einen Ablehnungsgrund aus.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload: DirectorDecisionCreatePayload = {
        substitution_request_id: item.id,
        payment_type: 'private',
        decision_type: 'rejected',
        rejection_reason: rejectionReason,
        rejection_note: rejectionNote || undefined,
      }
      await directorApi.createDecision(payload)
      onSuccess()
    } catch (e: unknown) {
      const msg = extractErrorMessage(e, { loadError: 'Ein Fehler ist aufgetreten' })
      if (isDirectorDecisionConflict(e)) {
        setError('Diese Anfrage wurde bereits von einem anderen Direktor entschieden.')
        onConflict?.()
      } else {
        setError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Vertretung ablehnen" size="lg">
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{item.requesting_teacher_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.lesson_count} Lektionen
          </p>
          {item.reason && <p className="text-xs text-gray-500 italic mt-1">{item.reason}</p>}
        </div>

        {detailLoading && <p className="text-xs text-gray-500">Lektionen werden geladen...</p>}
        {detail && detail.lessons.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full text-xs bg-white" aria-label="Lektionen der Anfrage">
              <thead className="text-[10px] text-gray-500 uppercase bg-gray-100 border-b">
                <tr>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Datum</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Zeit</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Fach</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Klasse</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Raum</th>
                  <th scope="col" className="py-1.5 px-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortLessonsByDateTime(detail.lessons).map(l => {
                    const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']
                    const isAssigned = Boolean(
                      l.assigned_teacher_id && COMMITTED.includes(l.status ?? ''),
                    )
                    const time = lessonRange(l, '-')
                    return (
                      <tr key={l.id}>
                        <td className="py-1.5 px-2">{formatTableDate(l.date)}</td>
                        <td className="py-1.5 px-2 font-semibold">{time || '—'}</td>
                        <td className="py-1.5 px-2 font-medium">{l.subject_name || '—'}</td>
                        <td className="py-1.5 px-2">{l.klass_name || '—'}</td>
                        <td className="py-1.5 px-2">{l.room_name || '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          {isAssigned
                            ? `✓ ${l.assigned_teacher_name || 'Vergeben'}`
                            : l.status === 'cancelled'
                              ? '✗ Storniert'
                              : '○ Offen'}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ablehnungsgrund *</label>
          <select
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
          >
            <option value="">-- Wählen --</option>
            {(Array.isArray(config?.director_rejection_reasons)
              ? (config!.director_rejection_reasons as unknown[])
                  .map(r => typeof r === 'string' ? r : ((r as { reason?: string })?.reason ?? ''))
                  .filter(Boolean)
              : []).map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notiz (optional)</label>
          <textarea
            value={rejectionNote}
            onChange={e => setRejectionNote(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
            placeholder="Optionale Notiz..."
          />
        </div>

        {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
          >
            <XCircle className="w-4 h-4" />
            {submitting ? 'Speichern...' : 'Ablehnen'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </Modal>
  )
}
